import type { ProductSchema } from '#build/armani/product-schema'

/**
 * Extracts raw image URLs from product assets, sorted by priority.
 *
 * @param {ProductSchema['assets']} assets - The product assets to process.
 * @returns {string[]} An array of image URLs, sorted by priority.
 * @example
 * // Returns ['https://example.com/image1.jpg', 'https://example.com/image2.jpg']
 * getRawImages(productAssets)
 */
export const getRawImages = (assets: ProductSchema['assets']) => {
  const images: TResponsiveImage[] = []

  if (assets === undefined) return images

  assets.forEach(image => {
    if (image.type === 'jpg' && typeof image.URL === 'string') {
      images.push(image.URL)
    }
  })

  // order the images by priority
  images.sort((a, b) => {
    const priorityA = assets.find(image => image.URL === a)?.priority
    const priorityB = assets.find(image => image.URL === b)?.priority

    if (typeof priorityA === 'number' && typeof priorityB === 'number') {
      return priorityA - priorityB
    }

    return 0
  })

  return images
}

/**
 * Extracts and formats image filenames from product assets.
 *
 * @param {ProductSchema['assets']} assets - The product assets to process.
 * @returns {string[]} An array of image filenames.
 * @example
 * // Returns ['image1.jpg', 'image2.jpg']
 * getFormattedImages(productAssets)
 */
export const getFormattedImages = (assets: ProductSchema['assets']) => {
  return getRawImages(assets).map(image =>
    image.split('/').slice(-1).toString()
  )
}

/**
 * Extracts 3D model URLs from product assets.
 *
 * @param {ProductSchema['assets']} assets - The product assets to process.
 * @returns {string[]} An array of 3D model URLs (empty if no 3D model found).
 * @example
 * // Returns ['https://example.com/model.glb']
 * get3DModelURLs(productAssets)
 */
export const get3DModelURLs = (assets: ProductSchema['assets']) => {
  const modelURL = assets?.find(i => i.type === 'glb')?.URL
  return modelURL ? [modelURL] : []
}

/**
 * Extracts and formats video assets with their cover images.
 *
 * @param {ProductSchema['assets']} assets - The product assets to process.
 * @returns {TResponsiveVideo[]} An array of video objects with URL and cover image.
 * @example
 * // Returns [{ url: 'video.mov', cover: 'video.jpg' }]
 * getFormattedVideos(productAssets)
 */
export const getFormattedVideos = (assets: ProductSchema['assets']) => {
  const videos: TResponsiveVideo[] = []

  if (assets === undefined) return videos

  assets.forEach(video => {
    if (video.type === 'mov' && typeof video.URL === 'string') {
      const base = video.URL.substring(0, video.URL.lastIndexOf('.'))
      const cover = `${base}.jpg`
      videos.push({ url: video.URL, cover })
    }
  })

  return videos
}

/**
 * Extracts and formats 3D image information from product assets.
 *
 * @param {ProductSchema['assets']} assets - The product assets to process.
 * @returns {Object | undefined} An object with cloud and model information, or undefined.
 * @example
 * // Returns { cloud: 'cloudinary', model: 'product-model' }
 * getFormattedImage3d(productAssets)
 */
export const getFormattedImage3d = (assets: ProductSchema['assets']) => {
  const url = assets?.find(asset => asset.type === 'glb')?.URL
  if (url) {
    // this is quick and dirty but we don't have time to make a better regex
    const isStagingUrl = url.includes('cloudinary')
    const regex = isStagingUrl
      ? /^.*cloudinary.com\/(?<cloud>.*).image\/.*upload\/(?<model>.*).glb/
      : /^.*\/(?<model>.*).glb/

    const matchModel = url.match(regex)
    if (matchModel)
      return {
        cloud: isStagingUrl ? matchModel[1] : '',
        model: isStagingUrl ? matchModel[2] : matchModel[1],
      }
  }

  return undefined
}

/**
 * Generates an ordered gallery of images, videos, and 3D models with specific placement rules.
 *
 * Rules:
 * - If a video exists, it should be the second element of the gallery
 * - If multiple videos exist, the first is at the second position, others from the fourth
 * - If a 3D image exists and no video, it replaces the second image
 * - If there is a Video and a 3D image, the 3D image should NOT be visible
 *
 * @param {ProductSchema['assets']} assets - The product assets to process.
 * @returns {Array<{type: string, data: any}>} An ordered gallery of media items.
 * @example
 * // Returns an array of gallery items with specific ordering
 * getOrderedGallery(productAssets)
 */
export const getOrderedGallery = (assets: ProductSchema['assets']) => {
  const images = getFormattedImages(assets)
  const videoGallery = getFormattedVideos(assets)
  const image3dViz = getFormattedImage3d(assets)

  const gallery = images.map(image => {
    return {
      type: 'TResponsiveImage' as const,
      data: image,
    }
  })

  const video =
    videoGallery &&
    videoGallery.map(video => {
      return {
        type: 'TResponsiveVideo' as const,
        data: video,
      }
    })

  const viz3D = image3dViz && {
    type: 'TResponsiveImage3D' as const,
    data: image3dViz,
  }

  if (!gallery) {
    return []
  }

  if (video?.length) {
    if (video.length === 1) {
      return [gallery[0], video[0], ...gallery.slice(1)]
    } else {
      if (gallery.length === 1) {
        return [gallery[0], video[0], ...video.slice(1)]
      }

      return [
        gallery[0],
        video[0],
        gallery[1],
        ...gallery.slice(2),
        ...video.slice(1),
      ]
    }
  }

  if (viz3D) {
    return [gallery[0], viz3D, ...gallery.slice(1)]
  }

  return gallery
}
